import { AssignmentContent, Brand } from 'shared/api/assignment-service';
import { dateService } from 'shared/lib/date';
import { PAGES } from 'shared/config/constants';

import {
  ASSIGNMENT_LEVELS,
  AssignmentActionsClickProps,
  AssignmentActionsContextEventsPayload,
  AssignmentActionsResultEventsPayload,
  CardEventHandlers,
  ClassFilterResultEventsPayload,
  CommonXApiContextPayload,
  DefaultClickProps,
  eventService,
  GenerateAssignmentActionsResultEventPayloadOptions,
  GenerateCardEventHandlersOptions,
  GenerateUpdateAssignmentResultEventsPayload,
  GoToLessonContextEventPayload,
  LessonBreadcrumbContextEventPayload,
  ProductBreadcrumbContextEventPayload,
  ProductFilterResultEventsPayload,
  UnitBreadcrumbContextEventPayload,
  UpdateAssignmentResultEventsPayload,
  ViewWorkContextEventPayload,
  XApiEventKeys,
  XApiLessonDataProps,
} from '../../model';

export const itemTypeToProductMap = {
  Lesson: 'curriculum',
  Cardstack: 'classwork',
  desmos: 'desmos',
  TaughtLesson: 'classroom',
};

const generateLessonDataForEventPayload = ({
  classBk,
  classGrade,
  content,
  lessonStatus,
  section,
  assignmentBusinessKey,
  isRightClick,
  url,
  page,
}: XApiLessonDataProps): GoToLessonContextEventPayload['result'] => {
  return {
    section,
    classBk,
    classGrade,
    lessonStatus,
    isRightClick,
    url,
    page,
    assignmentBusinessKey,
    domain: 'domain' in content ? content.domain : undefined,
    unitBusinessKey: 'unitKey' in content ? content.unitKey : undefined,
    unitRevisionNumber:
      'unitRevision' in content ? Number(content.unitRevision) : undefined,
    unitRevisionAlias:
      'unitRevisionAlias' in content ? content.unitRevisionAlias : undefined,
    lessonBusinessKey: 'lessonKey' in content ? content.lessonKey : undefined,
    customContentId:
      'customContentId' in content ? content.customContentId : undefined,
    isCustom:
      'brand' in content &&
      (content.brand === Brand.CUSTOM || content.brand === Brand.CUSTOMIZED),
    activityId: 'activityId' in content ? content.activityId : undefined,
    instanceId: 'id' in content ? content.id : undefined,
  };
};

/*
 * Returns number of currently assigned cardstacks
 * when user opens assignment panel or edits cardstack assignment.
 * In other cases all actions are performed on whole assignment.
 * */
const getLessonCount = (
  eventType: XApiEventKeys,
  assignmentLevel: ASSIGNMENT_LEVELS,
  numberOfCardstacks: number,
) => {
  const cardstackEditEvents = [
    XApiEventKeys.assignmentEditSelected,
    XApiEventKeys.selectedUpdateAssignment,
  ];

  if (
    cardstackEditEvents.includes(eventType) &&
    assignmentLevel === ASSIGNMENT_LEVELS.CARDSTACK
  ) {
    return numberOfCardstacks;
  }

  return 1;
};

const getActiveCardstacksBusinessKeys = (
  content: AssignmentContent | AssignmentContent[],
) => {
  if (!Array.isArray(content)) return undefined;

  const cardstackBusinessKeys: string[] = [];

  content.forEach((contentItem) => {
    if (
      !contentItem.cardstackKey ||
      ('isActivityActive' in contentItem && !contentItem.isActivityActive)
    )
      return;

    cardstackBusinessKeys.push(contentItem.cardstackKey);
  });

  return cardstackBusinessKeys;
};

export const generateAssignmentActionsResultEventPayload = ({
  eventType,
  assignmentType,
  content,
  assignmentBusinessKeys,
  assignmentClassBks,
  assignmentStartDate,
  assignmentDueDate,
}: GenerateAssignmentActionsResultEventPayloadOptions): AssignmentActionsResultEventsPayload => {
  const currentDate = dateService.getDayJSObject();

  const assignedCardstackBusinessKeys =
    getActiveCardstacksBusinessKeys(content);

  const assignmentLevel =
    assignmentType === 'Cardstack'
      ? ASSIGNMENT_LEVELS.CARDSTACK
      : ASSIGNMENT_LEVELS.LESSON; // 'lesson' or 'cardstack'

  const lessonCount = getLessonCount(
    eventType,
    assignmentLevel,
    assignedCardstackBusinessKeys?.length ?? 0,
  );

  const noDueDate = !assignmentDueDate;

  const dueDateDayJSObject = assignmentDueDate
    ? dateService.getDayJSObject(assignmentDueDate)
    : undefined;

  const hasStartDatePassed = assignmentStartDate
    ? currentDate.valueOf() > assignmentStartDate
    : true;

  const startDateDayJSObject =
    assignmentStartDate && !hasStartDatePassed
      ? dateService.getDayJSObject(assignmentStartDate)
      : undefined;

  const dueDate = dueDateDayJSObject
    ? dueDateDayJSObject.format('YYYY-MM-DD')
    : null;
  const dueTime = dueDateDayJSObject
    ? dueDateDayJSObject.format('H-m-s')
    : null;
  const dueDateCurrentDate = dueDateDayJSObject
    ? dueDateDayJSObject.diff(currentDate, 'day')
    : undefined;

  const scheduledDate = startDateDayJSObject
    ? startDateDayJSObject.format('YYYY-MM-DD')
    : null;
  const scheduledTime = startDateDayJSObject
    ? startDateDayJSObject.format('H-m-s')
    : null;
  const scheduledDateCurrentDate = startDateDayJSObject
    ? startDateDayJSObject.diff(currentDate, 'day')
    : undefined;

  const scheduledImmediately = !assignmentStartDate;

  return {
    result: {
      assignmentBusinessKeys,
      assignmentLevel,
      noDueDate,
      dueDate,
      dueDateCurrentDate,
      dueTime,
      lessonCount,
      scheduledDate,
      scheduledTime,
      scheduledDateCurrentDate,
      scheduledImmediately,
      assignedCardstackBusinessKeys,
      assignedGroupBusinessKeys: assignmentClassBks,
      groupCount: assignmentClassBks.length,
    },
  };
};

export const generateUpdateAssignmentResultEventsPayload = ({
  assignment,
  newStartDate,
  newDueDate,
}: GenerateUpdateAssignmentResultEventsPayload): UpdateAssignmentResultEventsPayload => {
  const assignmentActionsResultEventsPayload =
    generateAssignmentActionsResultEventPayload({
      eventType: XApiEventKeys.selectedUpdateAssignment,
      assignmentType: assignment.type,
      content: assignment.content,
      assignmentBusinessKeys: assignment.businessKey,
      assignmentClassBks: assignment.classBks,
      assignmentDueDate: newDueDate,
      assignmentStartDate: newStartDate,
    });

  const changedScheduledDate = dateService.getHasDayChanged(
    assignment.startDate,
    newStartDate,
  );
  const changedScheduledTime = dateService.getHasTimeChanged(
    assignment.startDate,
    newStartDate,
  );
  const changedDueDate = dateService.getHasDayChanged(
    assignment.dueDate,
    newDueDate,
  );
  const changedDueTime = dateService.getHasTimeChanged(
    assignment.dueDate,
    newDueDate,
  );

  return {
    result: {
      ...assignmentActionsResultEventsPayload.result,
      changedScheduledDate,
      changedScheduledTime,
      changedDueDate,
      changedDueTime,
    },
  };
};

export const generateGoToLessonContextEventPayload = (
  props: XApiLessonDataProps,
): GoToLessonContextEventPayload => ({
  result: generateLessonDataForEventPayload(props),
});

export const generateViewWorkContextEventPayload = (
  props: XApiLessonDataProps,
): ViewWorkContextEventPayload => ({
  result: generateLessonDataForEventPayload(props),
});

export const generateProductBreadcrumbContextEventPayload = (
  props: XApiLessonDataProps,
): ProductBreadcrumbContextEventPayload => ({
  result: generateLessonDataForEventPayload(props),
});

export const generateUnitBreadcrumbContextEventPayload = (
  props: XApiLessonDataProps,
): UnitBreadcrumbContextEventPayload => ({
  result: generateLessonDataForEventPayload(props),
});

export const generateLessonBreadcrumbContextEventPayload = (
  props: XApiLessonDataProps,
): LessonBreadcrumbContextEventPayload => ({
  result: generateLessonDataForEventPayload(props),
});

export const generateAssignmentActionsContextEventPayload = (
  props: XApiLessonDataProps,
): AssignmentActionsContextEventsPayload => ({
  result: generateLessonDataForEventPayload(props),
});

export const generateProductFilterResultEventPayload = (
  props: ProductFilterResultEventsPayload['result'],
): ProductFilterResultEventsPayload => ({
  result: props,
});

export const generateClassFilterResultEventPayload = (
  props: ClassFilterResultEventsPayload['result'],
): ClassFilterResultEventsPayload => ({
  result: props,
});

export const generateCommonContextEventPayload = (
  props: CommonXApiContextPayload['result'],
): CommonXApiContextPayload => ({
  result: props,
});

export const generateCardEventHandlers = ({
  currentClassGrades,
  item,
}: GenerateCardEventHandlersOptions): CardEventHandlers => {
  const content = item.type === 'Cardstack' ? item.content[0] : item.content;
  const defaultLessonStatus =
    item.type === 'TaughtLesson' ? 'started-class' : 'assigned';
  const itemClassBk =
    item.type === 'TaughtLesson' ? item.classBk : item.classBks[0];

  const onGoToLessonClick = ({
    section,
    url,
    page,
    lessonStatus,
    isRightClick,
  }: DefaultClickProps) =>
    eventService.send(
      XApiEventKeys.goToLessonSelected,
      undefined,
      generateGoToLessonContextEventPayload({
        content,
        section,
        isRightClick,
        url,
        page,
        classBk: itemClassBk,
        classGrade: currentClassGrades,
        lessonStatus: lessonStatus ?? defaultLessonStatus,
        assignmentBusinessKey:
          item.type === 'TaughtLesson' ? item.id : item.businessKey,
      }),
    );

  const onViewWorkClick = ({
    section,
    url,
    page,
    isRightClick,
    lessonStatus,
  }: DefaultClickProps) =>
    eventService.send(
      XApiEventKeys.viewWork,
      undefined,
      generateViewWorkContextEventPayload({
        content,
        section,
        url,
        page,
        isRightClick,
        classBk: itemClassBk,
        classGrade: currentClassGrades,
        lessonStatus: lessonStatus ?? defaultLessonStatus,
        assignmentBusinessKey:
          item.type === 'TaughtLesson' ? item.id : item.businessKey,
      }),
    );

  const onProductBreadcrumbClick = ({
    lessonStatus,
    section,
    url,
    page,
    isRightClick,
  }: DefaultClickProps) =>
    eventService.send(
      XApiEventKeys.productBreadcrumbSelected,
      undefined,
      generateProductBreadcrumbContextEventPayload({
        content,
        isRightClick,
        url,
        page,
        section,
        classBk: itemClassBk,
        classGrade: currentClassGrades,
        lessonStatus: lessonStatus ?? defaultLessonStatus,
        assignmentBusinessKey:
          item.type === 'TaughtLesson' ? item.id : item.businessKey,
      }),
    );

  const onUnitBreadcrumbClick = ({
    lessonStatus,
    section,
    url,
    page,
    isRightClick,
  }: DefaultClickProps) =>
    eventService.send(
      XApiEventKeys.unitBreadcrumbSelected,
      undefined,
      generateUnitBreadcrumbContextEventPayload({
        content,
        isRightClick,
        url,
        page,
        section,
        classBk: itemClassBk,
        classGrade: currentClassGrades,
        lessonStatus: lessonStatus ?? defaultLessonStatus,
        assignmentBusinessKey:
          item.type === 'TaughtLesson' ? item.id : item.businessKey,
      }),
    );

  const onLessonBreadcrumbClick = ({
    lessonStatus,
    section,
    url,
    page,
    isRightClick,
  }: DefaultClickProps) =>
    eventService.send(
      XApiEventKeys.lessonBreadcrumbSelected,
      undefined,
      generateLessonBreadcrumbContextEventPayload({
        content,
        isRightClick,
        url,
        page,
        section,
        classBk: itemClassBk,
        classGrade: currentClassGrades,
        lessonStatus: lessonStatus ?? defaultLessonStatus,
        assignmentBusinessKey:
          item.type === 'TaughtLesson' ? item.id : item.businessKey,
      }),
    );

  // assignment should be passed on each call to include actual data after editing
  const onAssignmentActionsClick = ({
    eventType,
    assignment,
    lessonStatus,
    section,
    url,
    page,
    isRightClick,
  }: AssignmentActionsClickProps) => {
    if (assignment.type === 'TaughtLesson') return Promise.resolve();

    return eventService.send(
      eventType,
      generateAssignmentActionsResultEventPayload({
        eventType,
        assignmentType: assignment.type,
        content: assignment.content,
        assignmentBusinessKeys: assignment.businessKey,
        assignmentClassBks: assignment.classBks,
        assignmentDueDate: assignment.dueDate,
        assignmentStartDate: assignment.startDate,
      }),
      generateAssignmentActionsContextEventPayload({
        isRightClick,
        url,
        page,
        section,
        content:
          assignment.type === 'Cardstack'
            ? assignment.content[0]
            : assignment.content,
        classBk: assignment.classBks[0],
        classGrade: currentClassGrades,
        lessonStatus: lessonStatus ?? defaultLessonStatus,
        assignmentBusinessKey: assignment.businessKey,
      }),
    );
  };

  return {
    onGoToLessonClick,
    onViewWorkClick,
    onProductBreadcrumbClick,
    onUnitBreadcrumbClick,
    onLessonBreadcrumbClick,
    onAssignmentActionsClick,
  };
};

export const getPage = () => {
  const hashName = window.location.hash.split('/')[1];

  return Object.values(PAGES).includes(hashName) ? hashName : 'main';
};
